import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Box, Collapse, Dialog, IconButton, LinearProgress, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlagiarismOutlinedIcon from "@mui/icons-material/PlagiarismOutlined";
import toast from "react-hot-toast";
import axiosInstant from "../../api";
import { projectProductTestingSubmissionsEndpoint } from "../../api/endpoints";
import {
    PRODUCT_TESTING_SECTIONS,
    PRODUCT_TESTING_TOKENS,
    ProductTestingForm,
    apiErrorCode,
} from "./constants";

const T = PRODUCT_TESTING_TOKENS;

interface SubmissionExpert {
    user: { userid: number; displayName?: string; avatar?: string };
    forms: ProductTestingForm[];
}

interface Props {
    projectId: number | string;
    onClose: () => void;
}

const LOAD_ERRORS: Record<string, string> = {
    Forbidden: "You do not have access to this feedback.",
    ProjectNotFound: "That project no longer exists.",
};

const submittedOn = (value: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

const expertInitials = (name?: string) =>
    (name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");

export const ProductTestingSubmissionsModal: FC<Props> = ({ projectId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [experts, setExperts] = useState<SubmissionExpert[]>([]);
    const [expanded, setExpanded] = useState<string | null>(null);

    // Inline onClose props change identity every parent render — keep it out
    // of the load deps so store updates don't refetch the modal.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const load = useCallback(() => {
        setLoading(true);
        axiosInstant
            .get(projectProductTestingSubmissionsEndpoint(projectId))
            .then((res) => {
                const loaded: SubmissionExpert[] = res.data?.experts || [];
                setExperts(loaded);
                const first = loaded[0];
                setExpanded(
                    first && first.forms[0]
                        ? `${first.user.userid}-${first.forms[0].desireid}`
                        : null
                );
            })
            .catch((error) => {
                toast.error(
                    LOAD_ERRORS[apiErrorCode(error)] ||
                        "Could not load the submitted feedback."
                );
                onCloseRef.current();
            })
            .finally(() => setLoading(false));
    }, [projectId]);

    useEffect(() => load(), [load]);

    const totalForms = experts.reduce((sum, expert) => sum + expert.forms.length, 0);

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            PaperProps={{
                sx: {
                    width: 560,
                    maxWidth: "100%",
                    borderRadius: "18px",
                    boxShadow: "0 24px 70px rgba(0,0,0,.2)",
                    fontFamily: "CircularStd",
                    overflow: "hidden",
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: "18px 22px 16px",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "10px",
                            backgroundColor: T.goldTile,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <PlagiarismOutlinedIcon sx={{ fontSize: 19, color: T.goldInk }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: "CircularStd",
                                fontSize: 18,
                                fontWeight: 800,
                                color: T.ink,
                                letterSpacing: "-0.3px",
                            }}
                        >
                            Product Feedback
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: "2px" }}>
                            <LockOutlinedIcon sx={{ fontSize: 12, color: T.muted }} />
                            <Typography
                                sx={{ fontFamily: "CircularStd", fontSize: 12, color: T.muted }}
                            >
                                Read only, submitted by experts
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        width: 32,
                        height: 32,
                        border: `1px solid ${T.border}`,
                        color: T.muted,
                        flexShrink: 0,
                        "&:hover": { borderColor: T.ink, color: T.ink },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            <Box sx={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
                {loading && (
                    <Box sx={{ px: "22px", py: 5 }}>
                        <LinearProgress
                            sx={{
                                height: 3,
                                backgroundColor: T.rule,
                                "& .MuiLinearProgress-bar": { backgroundColor: T.goldSoft },
                            }}
                        />
                    </Box>
                )}

                {!loading && totalForms === 0 && (
                    <Typography
                        sx={{
                            fontFamily: "CircularStd",
                            textAlign: "center",
                            py: 6,
                            px: 3,
                            color: T.muted,
                            fontSize: 13.5,
                        }}
                    >
                        No expert has submitted product feedback for this project yet.
                    </Typography>
                )}

                {!loading &&
                    experts.map((expert) => (
                        <Box key={expert.user.userid}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    p: "13px 22px",
                                    backgroundColor: T.goldTile,
                                    borderTop: `1px solid ${T.rule}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 26,
                                        height: 26,
                                        borderRadius: "50%",
                                        flexShrink: 0,
                                        backgroundColor: "var(--surface)",
                                        border: `1px solid ${T.goldSoft}`,
                                        color: T.goldInk,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontFamily: "CircularStd",
                                        fontSize: 10.5,
                                        fontWeight: 800,
                                    }}
                                >
                                    {expertInitials(expert.user.displayName)}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: "CircularStd",
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        color: T.goldInk,
                                        minWidth: 0,
                                    }}
                                >
                                    {expert.user.displayName || `Expert ${expert.user.userid}`}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "CircularStd",
                                        fontSize: 11.5,
                                        color: T.sub,
                                        ml: "auto",
                                        flexShrink: 0,
                                    }}
                                >
                                    {expert.forms.length}{" "}
                                    {expert.forms.length === 1 ? "product" : "products"}
                                </Typography>
                            </Box>

                            {expert.forms.map((form) => {
                                const key = `${expert.user.userid}-${form.desireid}`;
                                const isOpen = expanded === key;
                                return (
                                    <Box key={key}>
                                        <Box
                                            onClick={() => setExpanded(isOpen ? null : key)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1.5,
                                                p: "14px 22px",
                                                backgroundColor: T.barBg,
                                                borderTop: "1px solid #F0EDE8",
                                                borderBottom: "1px solid #F0EDE8",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontSize: 14.5,
                                                        fontWeight: 700,
                                                        color: T.ink,
                                                    }}
                                                >
                                                    {form.product?.title || `Product ${form.desireid}`}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.75,
                                                        mt: 0.75,
                                                        fontFamily: "CircularStd",
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        padding: "3px 10px",
                                                        borderRadius: "20px",
                                                        color: T.doneInk,
                                                        backgroundColor: T.doneBg,
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            width: 6,
                                                            height: 6,
                                                            borderRadius: "50%",
                                                            backgroundColor: T.doneDot,
                                                        }}
                                                    />
                                                    Submitted {submittedOn(form.submitted_at)}
                                                </Box>
                                            </Box>
                                            <ExpandMoreIcon
                                                sx={{
                                                    fontSize: 20,
                                                    color: T.muted,
                                                    flexShrink: 0,
                                                    transition: "transform .2s",
                                                    transform: isOpen ? "rotate(180deg)" : "none",
                                                }}
                                            />
                                        </Box>

                                        <Collapse in={isOpen} unmountOnExit>
                                            <Box sx={{ p: "20px 22px 4px" }}>
                                                {PRODUCT_TESTING_SECTIONS.map((section) => (
                                                    <Box key={section.title} sx={{ mb: "22px" }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 1.25,
                                                                mb: 2,
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: 11,
                                                                    fontWeight: 800,
                                                                    color: T.goldSoft,
                                                                    letterSpacing: "0.5px",
                                                                }}
                                                            >
                                                                {section.num}
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: 12,
                                                                    fontWeight: 700,
                                                                    color: T.sub,
                                                                    textTransform: "uppercase",
                                                                    letterSpacing: "0.6px",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {section.title}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    height: "1px",
                                                                    backgroundColor: T.rule,
                                                                }}
                                                            />
                                                        </Box>

                                                        {section.questions.map((question) => {
                                                            const value = (
                                                                form.answers[question.field] || ""
                                                            ).trim();
                                                            return (
                                                                <Box
                                                                    key={question.field}
                                                                    sx={{ mb: "18px", pl: "2px" }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily: "CircularStd",
                                                                            fontSize: 13.5,
                                                                            fontWeight: 600,
                                                                            color: T.ink,
                                                                            lineHeight: 1.4,
                                                                            mb: "8px",
                                                                        }}
                                                                    >
                                                                        {question.label}
                                                                    </Typography>
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily: "CircularStd",
                                                                            fontSize: 13.5,
                                                                            lineHeight: 1.6,
                                                                            whiteSpace: "pre-line",
                                                                            padding: "11px 13px",
                                                                            borderRadius: "9px",
                                                                            border: `1px solid ${
                                                                                value
                                                                                    ? T.filledBorder
                                                                                    : T.border
                                                                            }`,
                                                                            backgroundColor: value
                                                                                ? T.filledBg
                                                                                : T.barBg,
                                                                            color: value
                                                                                ? T.ink
                                                                                : T.placeholder,
                                                                            fontStyle: value
                                                                                ? "normal"
                                                                                : "italic",
                                                                        }}
                                                                    >
                                                                        {value || "No answer given"}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
            </Box>
        </Dialog>
    );
};
